import { ModalFooter } from '@common/mantine/components/modal-footer';
import { Button, Modal, Stack } from '@mantine/core';
import { MinstaySchema, RuleSchema } from '@pages/Client/Calendar/utils/validateBaseSettings';
import { t } from 'i18next';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useUpdatePricingSettings } from '@pages/Client/hooks/useUpdatePricingSettings';
import { zodResolver } from '@hookform/resolvers/zod';
import { RuleForm } from '@pages/Client/PricingStrategy/MinStay/components/RuleForm';
import { usePricingSettings } from '@pages/Client/hooks/usePricingSettings';
import {
  MinStay,
  MinStayRule,
  MinStayRules,
  ValidatedRule
} from '@pages/Client/PricingStrategy/MinStay/types';
import { useEventTracking } from '@common/hooks/useEventTracking';
import { useMinStaySettings, useSaveMinStaySettings } from '@pages/Client/hooks/useMinStaySettings';
import { useAppFlags } from '@common/hooks/useAppFlags';
import { extractMinStayDates } from '@pages/Client/Calendar/utils/extractMinStayDates';
import { useHandleMinStaySettings } from '@pages/Client/Calendar/hooks/useHandleMinStaySettings';

type RuleModalProps = {
  isOpen: boolean;
  onClose: () => void;
  mode: 'add' | 'edit';
  priority?: number | null;
};

export const createUniqueRuleSchema = (
  currentRules: MinStayRule[],
  editMode: boolean,
  currentPriority?: number
) => {
  return RuleSchema.extend({
    name: z
      .string()
      .nonempty('Please enter a name')
      .refine(
        (name) => {
          return !currentRules.some(
            (rule) => rule.name === name && (!editMode || rule.priority !== currentPriority)
          );
        },
        { message: 'This name is already in use' }
      )
  });
};

export const createUniqueMinstaySchema = (
  currentRules: MinStayRule[],
  editMode: boolean,
  currentPriority?: number
) => {
  const UniqueRuleSchema = createUniqueRuleSchema(currentRules, editMode, currentPriority);
  return MinstaySchema.extend({
    rules: z.array(UniqueRuleSchema).optional()
  });
};

export const RuleModal = ({ isOpen, onClose, mode, priority }: RuleModalProps) => {
  const { isMinStayV3Enabled } = useAppFlags();
  const { pricingSettings, pricingSettingsQuery } = usePricingSettings();
  const { minStaySettings } = useMinStaySettings();
  const { saveMinStaySettings } = useSaveMinStaySettings();
  const { prepareMinStaySettings } = useHandleMinStaySettings();

  const { savePricingSettings, isLoading: isSavePricingLoading } = useUpdatePricingSettings();
  const { isLoading: isSaveMinStayLoading } = useSaveMinStaySettings();
  const { trackEvent } = useEventTracking();

  const settings = isMinStayV3Enabled ? minStaySettings : pricingSettings;

  const rules = settings?.minstay?.rules || [];
  // Applicable when mode is 'edit'
  const ruleToEdit = rules.find((rule) => rule.priority === priority);

  const uniqueMinstaySchema = createUniqueMinstaySchema(
    rules,
    mode === 'edit',
    ruleToEdit?.priority
  );

  const methods = useForm<MinStay>({
    resolver: zodResolver(uniqueMinstaySchema)
  });

  const { fields: fieldFields, remove: removeField } = useFieldArray({
    control: methods.control,
    name: 'rules.0.fields'
  });

  const updateRuleByPriority = (
    rules: MinStayRules,
    priority: number,
    updates: ValidatedRule
  ): ValidatedRule[] => {
    if (!rules) return [updates];

    return rules?.map((rule) => {
      if (rule.priority === priority) {
        return { ...rule, ...updates };
      }
      return rule as ValidatedRule;
    });
  };

  const handleClose = () => {
    methods.reset();
    fieldFields.forEach((_, fieldIndex) => removeField(fieldIndex));
    onClose();
  };

  const saveRules = async (data: MinStay) => {
    if (!data.rules || data.rules.length === 0) {
      return;
    }

    const validatedRule = RuleSchema.parse(data.rules[0]);

    // Always get the latest data first to avoid race conditions
    const { data: latestPricingSettings } = await pricingSettingsQuery.refetch();

    // Ensure we have the latest data
    if (!latestPricingSettings) {
      throw new Error('Failed to fetch latest settings');
    }

    const updatedRules =
      mode === 'edit' && priority
        ? updateRuleByPriority(latestPricingSettings?.minstay?.rules || [], priority, validatedRule)
        : latestPricingSettings?.minstay?.rules
          ? [...latestPricingSettings.minstay.rules, validatedRule]
          : [validatedRule];

    // Update pricing settings - preserve all existing data
    const newPricingSettings = {
      ...latestPricingSettings,
      minstay: {
        ...latestPricingSettings?.minstay,
        rules: updatedRules
      }
    };

    const minStayDates = extractMinStayDates(newPricingSettings.dates);
    const { minStaySettingsForAPI } = await prepareMinStaySettings(minStayDates);

    const newMinStaySettings = {
      ...minStaySettingsForAPI,
      minstay: {
        ...minStaySettingsForAPI?.minstay,
        rules: updatedRules
      }
    };

    // Save pricing settings first
    await savePricingSettings(JSON.stringify(newPricingSettings));
    await saveMinStaySettings(JSON.stringify(newMinStaySettings));

    trackEvent('MinStayAutomationApplied', {
      tags: {
        mode,
        type: validatedRule.fields[0].type === 'single' ? 'single' : 'multiple',
        field: validatedRule.fields[0].type === 'single' ? validatedRule.fields[0].field : ''
      }
    });
    handleClose();
  };

  const isLoading = isSavePricingLoading || isSaveMinStayLoading;

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      title={
        mode === 'add'
          ? t('Removal Criteria')
          : t('Edit Removal Criteria "{{ruleName}}"', { ruleName: ruleToEdit?.name })
      }
      size="lg"
      centered
      styles={{
        body: {
          padding: '0'
        }
      }}
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(saveRules)}>
          <Stack px="md" pb="md" gap="lg">
            <RuleForm ruleToEdit={ruleToEdit} mode={mode} />
          </Stack>
          <ModalFooter>
            <Button onClick={handleClose} variant="subtle">
              {t('Cancel')}
            </Button>
            <Button type="submit" loading={isLoading}>
              {t('Save')}
            </Button>
          </ModalFooter>
        </form>
      </FormProvider>
    </Modal>
  );
};
